import { useState } from "react";
import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import { BLOG_POSTS } from "../../data/blogData";

const ALL_CATEGORIES = ["All", ...Array.from(new Set(BLOG_POSTS.map((p) => p.category)))];

export function BlogPage() {
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === active);

  return (
    <div className="min-h-screen bg-[#F6F2EB]">
      <section className="py-20 bg-[#F6F2EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-14">
            <h1 className="text-5xl font-semibold text-[#131312] mb-3">Blog</h1>
            <p className="text-muted-foreground text-lg max-w-xl">Expert insights, industry trends, and practical packaging guides from the Colour Club team.</p>
          </div>

          <div className="flex flex-col md:flex-row gap-12">
            {/* Category sidebar */}
            <aside className="w-full md:w-52 flex-shrink-0">
              <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Categories</p>
              <nav className="flex flex-row flex-wrap gap-2 md:flex-col md:gap-1">
                {ALL_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActive(cat)}
                    className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active === cat
                        ? "bg-[#131312] text-white"
                        : "text-muted-foreground hover:text-[#131312] hover:bg-black/[0.05]"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </nav>
            </aside>

            {/* Posts grid */}
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {filtered.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:shadow-xl"
                  >
                    <div className="relative aspect-[16/9] w-full overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="flex flex-col gap-3 p-8">
                      <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{post.category}</span>
                      <h2 className="text-2xl font-semibold leading-snug text-[#131312]">{post.title}</h2>
                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{post.author}</span>
                          <span>·</span>
                          <time>{post.date}</time>
                        </div>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-[#131312]">
                          Read article <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
